import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, query, where } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import "moment/locale/fr";

moment.locale("fr");

const STATUS_LABELS = {
  pending: "En attente",
  reviewed: "Examiné",
  resolved: "Résolu",
  dismissed: "Rejeté",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  reviewed: "bg-blue-100 text-blue-800 border-blue-200",
  resolved: "bg-green-100 text-green-800 border-green-200",
  dismissed: "bg-gray-100 text-gray-800 border-gray-200",
};

// fonction pour tronquer texte
function truncate(text, max = 60) {
  if (!text) return "-";
  return text.length > max ? text.slice(0, max) + "\u2026" : text;
}

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    async function fetchReports() {
      setLoading(true);
      const snap = await getDocs(collection(db, "reports"));
      const data = [];

      for (const docSnap of snap.docs) {
        const report = docSnap.data();

        let reporterName = "";
        let reporterEmail = "";
        let reporterVerified = false;
        let targetName = "";
        let targetEmail = "";
        let targetIsActive = true;

        // Le champ reporter est "userId" côté mobile, "reporterId" en legacy
        const reporterUid = report.userId || report.reporterId;

        if (reporterUid && typeof reporterUid === "string") {
          const reporterSnap = await getDoc(doc(db, "users", reporterUid));
          if (reporterSnap.exists()) {
            const u = reporterSnap.data();
            reporterName = `${u.firstname || ""} ${u.lastname || ""}`.trim();
            reporterEmail = u.email || "";
            reporterVerified = u.emailIsVerified === true;
          }
        }
        if (report.targetUserId && typeof report.targetUserId === "string") {
          const targetSnap = await getDoc(doc(db, "users", report.targetUserId));
          if (targetSnap.exists()) {
            const u = targetSnap.data();
            targetName = `${u.firstname || ""} ${u.lastname || ""}`.trim();
            targetEmail = u.email || "";
            targetIsActive = u.isActive !== false;
          }
        }

        data.push({
          id: docSnap.id,
          reporterUid,
          reporterName,
          reporterEmail,
          reporterVerified,
          targetUserId: report.targetUserId || null,
          targetName,
          targetEmail,
          targetIsActive,
          reason: report.subject || report.reason || "",
          message: report.message || "",
          status: report.status || "pending",
          createdAt: report.createdAt,
          projectId: report.projectId || null,
          offerId: report.offerId || null,
          type: report.type || null,
        });
      }

      // Trier par date de création (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setReports(data);
      setLoading(false);
    }
    fetchReports();
  }, []);

  const filteredReports =
    filterStatus === "all"
      ? reports
      : reports.filter((r) => r.status === filterStatus);

  const statusCounts = {
    all: reports.length,
    pending: reports.filter((r) => r.status === "pending").length,
    reviewed: reports.filter((r) => r.status === "reviewed").length,
    resolved: reports.filter((r) => r.status === "resolved").length,
    dismissed: reports.filter((r) => r.status === "dismissed").length,
  };

  // Compter les signalements par utilisateurs vérifiés pour chaque cible
  const getVerifiedReportCount = (targetUserId) => {
    if (!targetUserId) return 0;
    const uniqueVerifiedReporters = new Set();
    reports.forEach((r) => {
      if (r.targetUserId === targetUserId && r.reporterVerified && r.reporterUid) {
        uniqueVerifiedReporters.add(r.reporterUid);
      }
    });
    return uniqueVerifiedReporters.size;
  };

  const disableAccount = async (targetUserId, targetName) => {
    if (!window.confirm(`Désactiver le compte de ${targetName || "cet utilisateur"} ?`)) return;
    try {
      await updateDoc(doc(db, "users", targetUserId), { isActive: false });
      setReports((prev) =>
        prev.map((r) =>
          r.targetUserId === targetUserId ? { ...r, targetIsActive: false } : r
        )
      );

      const div = document.createElement("div");
      div.className =
        "fixed top-4 right-4 bg-orange-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 text-sm";
      div.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
        Compte de ${targetName || "l'utilisateur"} désactivé !
      `;
      document.body.appendChild(div);
      setTimeout(() => div.remove(), 4000);
    } catch {
      alert("Erreur lors de la désactivation du compte");
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Confirmez-vous la suppression de ce signalement ?")) return;
    try {
      await deleteDoc(doc(db, "reports", id));
      setReports((prev) => prev.filter((r) => r.id !== id));

      const div = document.createElement("div");
      div.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 text-sm";
      div.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Signalement supprimé avec succès !
      `;
      document.body.appendChild(div);
      setTimeout(() => div.remove(), 3000);
    } catch {
      alert("Erreur lors de la suppression");
    }
  };

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      const ref = doc(db, "reports", reportId);
      await updateDoc(ref, { status: newStatus });
      setReports((prev) =>
        prev.map((r) => (r.id === reportId ? { ...r, status: newStatus } : r))
      );

      const div = document.createElement("div");
      div.className =
        "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
      div.textContent = "Statut mis à jour !";
      document.body.appendChild(div);
      setTimeout(() => div.remove(), 3000);
    } catch {
      alert("Erreur lors de la mise à jour du statut");
    }
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Filter tabs */}
          <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
            {[
              { key: "all", label: "Tous" },
              { key: "pending", label: "En attente" },
              { key: "reviewed", label: "Examinés" },
              { key: "resolved", label: "Résolus" },
              { key: "dismissed", label: "Rejetés" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filterStatus === key
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label} ({statusCounts[key]})
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            {filteredReports.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucun signalement trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Signaleur</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur signalé</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Raison</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredReports.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5">
                          {r.reporterName ? (
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {r.reporterName}
                                {r.reporterVerified && (
                                  <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200">
                                    Vérifié
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-400">{r.reporterEmail}</p>
                            </div>
                          ) : (
                            <span className="text-xs italic text-gray-400">Inconnu</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {r.targetName ? (
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {r.targetName}
                                {!r.targetIsActive && (
                                  <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 border border-red-200">
                                    Désactivé
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-400">{r.targetEmail}</p>
                              {r.targetUserId && (() => {
                                const count = getVerifiedReportCount(r.targetUserId);
                                if (count === 0) return null;
                                return (
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <span className={`text-[10px] font-medium ${count >= 3 ? "text-red-600" : "text-orange-500"}`}>
                                      {count} signalement{count > 1 ? "s" : ""} vérifié{count > 1 ? "s" : ""}
                                    </span>
                                    {count >= 3 && r.targetIsActive && (
                                      <button
                                        onClick={() => disableAccount(r.targetUserId, r.targetName)}
                                        className="text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 px-1.5 py-0.5 rounded"
                                      >
                                        Désactiver
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <span className="text-xs italic text-gray-400">Inconnu</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 max-w-xs" title={r.reason}>
                          <span className="text-xs text-gray-600">{truncate(r.reason, 60)}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS[r.status] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
                            {STATUS_LABELS[r.status] || r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                          {r.createdAt && r.createdAt.toDate
                            ? moment(r.createdAt.toDate()).format("DD/MM/YYYY HH:mm")
                            : r.createdAt ? moment(r.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <select
                              value={r.status}
                              onChange={(e) => handleStatusChange(r.id, e.target.value)}
                              className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                            >
                              {Object.keys(STATUS_LABELS).map((key) => (
                                <option key={key} value={key}>{STATUS_LABELS[key]}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => deleteReport(r.id)}
                              className="text-xs font-medium text-red-600 hover:text-red-800"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
